import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent, SlideshowSelectEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';
import { onDocumentLoaded, requestIdleCallback } from '@theme/utilities';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    this.#scheduleHeldImages(signal);
  }

  /**
   * Slides after the first ship as 40px placeholders with the real
   * src/srcset/sizes in data-held-* (hold_image in snippets/product-media.liquid),
   * so they stop splitting the main photo's bandwidth. They swap in once the
   * page has loaded and gone idle, or at once on the first sign someone is using
   * the gallery. A gallery that replaces this one after a variant change runs
   * this on its own connect; the page is loaded by then, so it swaps at the next
   * idle moment.
   *
   * @param {AbortSignal} signal
   */
  #scheduleHeldImages(signal) {
    if (!this.querySelector('img[data-held-srcset]')) return;

    for (const type of ['pointerdown', 'pointerover', 'focusin']) {
      this.addEventListener(type, this.#releaseHeldImages, { once: true, signal });
    }
    this.addEventListener(
      SlideshowSelectEvent.eventName,
      (event) => {
        if (event instanceof SlideshowSelectEvent && event.detail.index !== 0) this.#releaseHeldImages();
      },
      { signal }
    );

    onDocumentLoaded(() => requestIdleCallback(this.#releaseHeldImages, { timeout: 2000 }));
  }

  #releaseHeldImages = () => {
    // A detached <img> still downloads, so a gallery replaced by a variant
    // change must not swap.
    if (!this.isConnected) return;

    for (const img of this.querySelectorAll('img[data-held-srcset]')) {
      if (!(img instanceof HTMLImageElement)) continue;
      const { heldSrc, heldSrcset, heldSizes } = img.dataset;

      // sizes before srcset: the file is chosen the moment srcset is set. The
      // img keeps loading="lazy", so slides far to the side still wait.
      if (heldSizes) img.sizes = heldSizes;
      if (heldSrcset) img.srcset = heldSrcset;
      if (heldSrc) img.src = heldSrc;

      delete img.dataset.heldSizes;
      delete img.dataset.heldSrcset;
      delete img.dataset.heldSrc;
    }
  };

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
